import styled from '@emotion/styled';
import { color, spacing, typography } from '@uniquegood/realworld-studio-design';
import { getAccessToken } from '@/auth';
import { ClickEventName, track } from '@/track';

interface CTASectionProps {
  title: string;
  subtitle: string;
  buttons: {
    text: string;
    variant: 'primary' | 'secondary';
    action: string;
  }[];
}

const CTASection = ({ title, subtitle, buttons }: CTASectionProps) => {
  const handleButtonClick = async (action: string) => {
    const accessToken = await getAccessToken();

    if (action.startsWith('http')) {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan_banner_class_purchase,
        params: { is_logged_in: accessToken ? 'true' : 'false' }
      });
      window.open(action, '_blank', 'noopener,noreferrer');
    } else {
      track.onClick({
        clickEventName: ClickEventName.click_landing_plan_banner_enterprise_contact,
        params: { is_logged_in: accessToken ? 'true' : 'false' }
      });
      window.open(action, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <SectionContainer>
      <Card>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <ButtonGroup>
          {buttons.map((button) => (
            <CTAButton
              key={button.text}
              variant={button.variant}
              onClick={() => handleButtonClick(button.action)}
              type="button"
            >
              {button.text}
            </CTAButton>
          ))}
        </ButtonGroup>
      </Card>
    </SectionContainer>
  );
};

export default CTASection;

const SectionContainer = styled.section`
  width: 100%;
  padding: ${spacing.padding.xlarge3} ${spacing.padding.xlarge2};
  background: #f5f5f5;
  margin-bottom: 180px;
`;

const Card = styled.div`
  max-width: 920px;
  margin: 0 auto;
  background: #333333;
  border-radius: 16px;
  padding: 48px 24px 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 26px;
  font-weight: ${typography.weight.medium};
  line-height: 32px;
  color: ${color.white};
  text-align: center;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: ${typography.weight.regular};
  line-height: 20px;
  color: ${color.white};
  text-align: center;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const CTAButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  white-space: nowrap;

  ${({ variant }) => {
    if (variant === 'primary') {
      return `
        background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
        color: ${color.white};
        &:hover {
          opacity: 0.9;
        }
      `;
    }
    return `
      background: #e8e8e8;
      color: #1a1a1a;
      &:hover {
        background: #d8d8d8;
      }
    `;
  }}
`;
